import React, { useState, useEffect, Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { CheckCircleIcon, XCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/solid'
import tools from '../../tools'
import connexion from '../../connexion'

// Consultation (lecture seule) d'un inventaire du personnel depuis la liste des inventaires : mêmes onglets
// que la fiche d'un inventaire client (entrée, sortie, photos, commentaires, détails). Les inventaires du
// personnel se modifient depuis l'application terrain (/inventaires-terrain), pas ici.
// row : une ligne de la liste (staff_checkin_inventory, staff_checkout_inventory ou staff_free_inventory).

const TABS_BADGE = 'hidden ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block bg-purple-100 text-purple-600'

function getJson(path)
{
	return fetch(connexion.connect.url + path).then(response => response.json())
}

function personName(user)
{
	if (user && typeof user === 'object')
		return ((user.firstname || '') + ' ' + (user.lastname || '')).trim() || 'Personnel'
	return 'Personnel'
}

function classNames(...classes)
{
	return classes.filter(Boolean).join(' ')
}

function plural(count, singular, pluralForm)
{
	return count + ' ' + (count > 1 ? (pluralForm || singular + 's') : singular)
}

function gapCount(inventory)
{
	let gaps = 0
	;(inventory.inventory || []).forEach(theme => {
		;(theme.objects || []).forEach(object => {
			if (Number(object.count) !== Number(object.quantity))
				gaps++
		})
	})
	return gaps
}

function kindLabel(inventory)
{
	if (inventory.type === 'arrival')
		return 'Entrée'
	if (inventory.type === 'departure')
		return 'Sortie'
	return inventory.tour_id ? 'Tour de terrain' : 'Hors séjour'
}

export function Checklist({ inventory })
{
	const themes = (inventory.inventory || []).filter(theme => theme.objects && theme.objects.length > 0)
	const gaps = gapCount(inventory)
	return (
		<div className="px-4 sm:px-6 lg:px-8 pb-10 overflow-y-auto">
			<div className="flex flex-wrap items-center gap-3 my-5 text-base text-gray-700">
				<span className="font-semibold text-gray-900">{kindLabel(inventory)}</span>
				<span>{tools.parseDate(inventory.update_time || inventory.create_time)}</span>
				<span>{'par ' + personName(inventory.update_user && typeof inventory.update_user === 'object' ? inventory.update_user : inventory.create_user)}</span>
				{inventory.version > 1 && <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">{'version ' + inventory.version}</span>}
				{inventory.verdict === 'validated' && <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">Validé</span>}
				{inventory.verdict === 'disputed' && <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-700">Contesté</span>}
				<span className={gaps > 0 ? 'text-yellow-700' : 'text-green-700'}>{gaps > 0 ? plural(gaps, 'écart') : 'Aucun écart'}</span>
			</div>
			{themes.length === 0 ? (
				<p className="text-base text-gray-700 italic">Aucun comptage : seuls des photos ou des commentaires ont été ajoutés.</p>
			) : (
				<table className="bg-white table-auto divide-y divide-gray-200 rounded-lg w-full">
					<thead>
						<tr>
							<th scope="col" className="px-3 py-3.5 text-left text-base font-semibold text-gray-900">Désignation</th>
							<th scope="col" className="px-3 py-3.5 text-left text-base font-semibold text-gray-900">{kindLabel(inventory)}</th>
							<th scope="col" className="px-3 py-3.5 text-left text-base font-semibold text-gray-900">Attendu</th>
							<th scope="col" className="px-3 py-3.5 text-left text-base font-semibold text-gray-900">Conforme</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-200">
						{themes.map((theme, theme_id) => (
							<Fragment key={theme_id}>
								<tr className="border-t border-gray-200 bg-gray-100">
									<th colSpan={4} scope="colgroup" className="px-4 py-2 text-left text-base font-bold text-gray-900 sm:px-6">{theme.name}</th>
								</tr>
								{theme.objects.map((object, object_id) => {
									const count = Number(object.count)
									const quantity = Number(object.quantity)
									const known = object.count !== null && object.count !== undefined && object.count !== '' && !isNaN(count)
									let status = <QuestionMarkCircleIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" aria-hidden="true" />
									if (known)
										status = (count === quantity)
											? <CheckCircleIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-green-400" aria-hidden="true" />
											: <XCircleIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-red-400" aria-hidden="true" />
									return (
										<tr key={object_id} className={known && count !== quantity ? 'border-t bg-yellow-50' : ''}>
											<td className="whitespace-nowrap px-3 py-4 text-base text-gray-900">{object.name}</td>
											<td className="whitespace-nowrap px-3 py-4 text-base text-gray-900">{known ? count : 'NC'}</td>
											<td className="whitespace-nowrap px-3 py-4 text-base text-gray-500">{quantity}</td>
											<td className="whitespace-nowrap px-3 py-4 text-base text-gray-500">{status}</td>
										</tr>
									)
								})}
							</Fragment>
						))}
					</tbody>
				</table>
			)}
		</div>
	)
}

export function Photos({ inventories })
{
	const items = []
	inventories.forEach(inventory => {
		;(inventory.pictures || []).forEach(picture => {
			if (picture && typeof picture === 'object' && picture._id && !picture.deleted_time)
				items.push({ picture, inventory })
		})
	})
	if (items.length === 0)
		return <p className="px-4 sm:px-6 lg:px-8 my-5 text-base text-gray-700 italic">Pas de photos postées.</p>
	return (
		<ul className="grid grid-cols-2 gap-x-4 gap-y-8 px-4 sm:px-6 lg:px-8 my-5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 overflow-y-auto">
			{items.map(({ picture, inventory }, index) => (
				<li key={String(picture._id) + '-' + index} className="relative">
					<a
						href={connexion.connect.url + 'dashboard/files/get-image/' + String(picture._id)}
						target="_blank"
						rel="noopener noreferrer"
						className="group block w-full aspect-w-10 aspect-h-7 rounded-lg bg-gray-100 overflow-hidden focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
					>
						<img src={connexion.connect.url + 'dashboard/files/get-image/' + String(picture._id) + '/360'} alt={picture.description || ''} className="object-cover pointer-events-none group-hover:opacity-75" />
					</a>
					{picture.description ? <p className="mt-2 block text-sm font-medium text-gray-900">{picture.description}</p> : null}
					<p className="pointer-events-none mt-1 block truncate text-sm font-medium text-gray-900">{personName(picture.user)}</p>
					<p className="pointer-events-none block text-sm font-medium text-gray-500">{[kindLabel(inventory), picture.theme, picture.timestamp ? tools.parseDate(picture.timestamp) : null].filter(Boolean).join(' · ')}</p>
					{picture.after ? <p className="pointer-events-none block text-xs text-gray-400">Ajoutée après l'enregistrement</p> : null}
				</li>
			))}
		</ul>
	)
}

export function Comments({ inventories })
{
	const items = []
	inventories.forEach(inventory => {
		;(inventory.comment || []).forEach(comment => {
			if (comment && comment.comment && !comment.deleted_time)
				items.push({ comment, inventory })
		})
	})
	items.sort((a, b) => (a.comment.timestamp || 0) - (b.comment.timestamp || 0))
	if (items.length === 0)
		return <p className="px-4 sm:px-6 lg:px-8 my-5 text-base text-gray-700 italic">Pas de commentaires.</p>
	return (
		<ul className="px-4 sm:px-6 lg:px-8 my-5 divide-y divide-gray-200 overflow-y-auto">
			{items.map(({ comment, inventory }, index) => (
				<li key={index} className="py-4">
					<p className="text-base text-gray-900 whitespace-pre-wrap">{comment.comment}</p>
					<p className="mt-1 text-sm text-gray-500">
						{[personName(comment.user), kindLabel(inventory), comment.theme, comment.timestamp ? tools.parseDate(comment.timestamp) : null].filter(Boolean).join(' · ')}
						{comment.update_time ? ' · modifié' : ''}
						{comment.after ? ' · ajouté après l\'enregistrement' : ''}
					</p>
				</li>
			))}
		</ul>
	)
}

function Row({ label, children, grey })
{
	return (
		<div className={classNames(grey ? 'bg-gray-50' : 'bg-white', 'px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6')}>
			<dt className="text-base font-medium text-gray-500">{label}</dt>
			<dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">{children}</dd>
		</div>
	)
}

export function Details({ inventories, versions })
{
	const first = inventories[0]
	const stay = first.stay || null
	const client = first.client || null
	const contributors = {}
	inventories.forEach(inventory => {
		;(inventory.contributors || []).forEach(user => {
			contributors[user && user._id ? String(user._id) : String(user)] = personName(user)
		})
	})
	return (
		<div className="border-t border-gray-200 overflow-y-auto">
			<dl>
				<Row label="Emplacement">{first.location || (stay ? stay.location : '-')}</Row>
				{stay && stay.location_category ? <Row label="Hébergement" grey>{stay.location_category}</Row> : null}
				<Row label="Séjour" grey>{first.stay_number ? first.stay_number : 'Hors séjour'}</Row>
				{client ? <Row label="Vacancier">{personName(client)}</Row> : null}
				{inventories.map(inventory => (
					<Row key={String(inventory._id)} label={kindLabel(inventory)} grey>
						{tools.parseDate(inventory.create_time)} par {personName(inventory.create_user)}
						{inventory.version > 1 ? ', ' + plural(inventory.version - 1, 'modification') + ' (dernière le ' + tools.parseDate(inventory.update_time) + ' par ' + personName(inventory.update_user) + ')' : ''}
					</Row>
				))}
				{Object.keys(contributors).length > 0 ? <Row label="Participants">{Object.values(contributors).join(', ')}</Row> : null}
			</dl>
			{inventories.map(inventory => {
				const list = versions[String(inventory._id)] || []
				if (list.length === 0)
					return null
				return (
					<div key={String(inventory._id)} className="px-4 py-5 sm:px-6">
						<h3 className="text-base font-semibold text-gray-900 mt-0">{'Versions précédentes · ' + kindLabel(inventory)}</h3>
						<ul className="mt-2 divide-y divide-gray-200">
							{list.map(version => (
								<li key={version.version} className="py-3 text-base text-gray-700">
									{'Version ' + version.version + ' · ' + tools.parseDate(version.update_time) + ' par ' + personName(version.update_user) + ' · ' + (gapCount(version) > 0 ? plural(gapCount(version), 'écart') : 'aucun écart') + ', remplacée le ' + tools.parseDate(version.archived_time) + ' par ' + personName(version.archived_user)}
								</li>
							))}
						</ul>
					</div>
				)
			})}
			<div className="px-4 py-3" />
		</div>
	)
}

export default function InventoryStaffView({ row, onClose })
{
	const [status, setStatus] = useState('loading')
	const [inventories, setInventories] = useState([])
	const [versions, setVersions] = useState({})
	const [tab, setTab] = useState(null)

	const free = row.staff_free_inventory

	useEffect(() => {
		let cancelled = false
		setStatus('loading')
		const request = row.stay_number
			? getJson('dashboard/inventories/find-by-stay-number?stay_number=' + encodeURIComponent(row.stay_number) + '&complete=true')
				.then(list => (Array.isArray(list) ? list.filter(inventory => inventory.source === 'staff') : Promise.reject(new Error('list'))))
			: getJson('dashboard/inventories/find-by-id?id=' + encodeURIComponent(free ? free._id : ''))
				.then(inventory => (inventory && inventory._id ? [inventory] : Promise.reject(new Error('inventory'))))
		request
			.then(list => {
				const order = { arrival: 0, free: 1, departure: 2 }
				list.sort((a, b) => (order[a.type] !== undefined ? order[a.type] : 3) - (order[b.type] !== undefined ? order[b.type] : 3))
				if (list.length === 0)
					return Promise.reject(new Error('empty'))
				return Promise.all(list.map(inventory => getJson('dashboard/inventories/versions?id=' + encodeURIComponent(inventory._id)).then(result => (Array.isArray(result) ? result : [])).catch(() => [])))
					.then(results => {
						if (cancelled)
							return ;
						const byId = {}
						list.forEach((inventory, index) => {
							byId[String(inventory._id)] = results[index]
						})
						setInventories(list)
						setVersions(byId)
						setTab(list[0]._id)
						setStatus('ready')
					})
			})
			.catch(() => {
				if (!cancelled)
					setStatus('error')
			})
		return () => {
			cancelled = true
		}
	}, [row, free])

	const picturesCount = inventories.reduce((sum, inventory) => sum + (inventory.pictures || []).filter(picture => picture && typeof picture === 'object' && picture._id && !picture.deleted_time).length, 0)
	const commentsCount = inventories.reduce((sum, inventory) => sum + (inventory.comment || []).filter(comment => comment && comment.comment && !comment.deleted_time).length, 0)
	const tabs = inventories.map(inventory => ({ key: String(inventory._id), label: inventory.type === 'arrival' ? 'Inventaire d\'entrée' : (inventory.type === 'departure' ? 'Inventaire de sortie' : 'Inventaire'), badge: gapCount(inventory) || null }))
		.concat([
			{ key: 'photos', label: 'Photos', badge: picturesCount || null },
			{ key: 'comments', label: 'Commentaires', badge: commentsCount || null },
			{ key: 'details', label: 'Détails', badge: null }
		])
	const current = tab === null ? null : String(tab)
	const currentInventory = inventories.find(inventory => String(inventory._id) === current) || null

	let content = null
	if (status === 'loading')
		content = <p className="px-6 py-10 text-base text-gray-500">Chargement de l'inventaire…</p>
	else if (status === 'error')
		content = <p className="px-6 py-10 text-base text-red-600">Impossible de charger l'inventaire du personnel. Réessayez dans un instant.</p>
	else if (currentInventory)
		content = <Checklist inventory={currentInventory} />
	else if (current === 'photos')
		content = <Photos inventories={inventories} />
	else if (current === 'comments')
		content = <Comments inventories={inventories} />
	else if (current === 'details')
		content = <Details inventories={inventories} versions={versions} />

	return (
		<Transition appear show={true} as={Fragment}>
			<Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={onClose}>
				<div className="flex items-center justify-center min-h-screen px-4 text-center sm:block sm:p-0">
					<Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
						<Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
					</Transition.Child>
					<span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
					<Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
						<div className="overflow-hidden inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all sm:align-middle lg:max-w-5xl sm:w-full">
							<div className="bg-white px-4 py-5 sm:px-6 flex items-center justify-between">
								<h3 className="text-xl leading-6 my-0 font-medium text-gray-900">
									{'Inventaire du personnel · ' + (row.stay && row.stay.location ? row.stay.location : '')}
									{row.stay_number ? ' · séjour ' + row.stay_number : ''}
								</h3>
								<button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none text-xl leading-none" aria-label="Fermer">×</button>
							</div>
							{status === 'ready' && (
								<div className="border-b border-gray-200 overflow-x-auto">
									<div className="-mb-px flex" aria-label="Onglets">
										{tabs.map(item => (
											<button
												key={item.key}
												type="button"
												onClick={() => setTab(item.key)}
												style={{ backgroundColor: 'transparent' }}
												className={classNames(
													current === item.key ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
													'flex-1 py-4 px-3 text-center border-b-2 font-medium text-sm whitespace-nowrap'
												)}
												aria-current={current === item.key ? 'page' : undefined}
											>
												<span>{item.label}</span>
												{item.badge ? <span className={TABS_BADGE}>{item.badge}</span> : null}
											</button>
										))}
									</div>
								</div>
							)}
							<div className="max-h-screen-80 overflow-y-auto" style={{ maxHeight: '70vh' }}>
								{content}
							</div>
							<div className="px-4 py-3 text-right sm:px-6 border-t border-gray-200">
								<button type="button" onClick={onClose} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
									Fermer
								</button>
							</div>
						</div>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition>
	)
}
